

import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import Layout from "@/components/layout/Layout";
import { Helmet } from "react-helmet";
import { toast } from "sonner";
import { SearchFilterBar } from "@/components/nursing-homes/SearchFilterBar";
import { CountyListings } from "@/components/nursing-homes/CountyListings";
import CTASection from "@/components/home/CTASection";
import { useCounties } from "@/hooks/use-counties";
import { SmartFilters } from "@/components/nursing-homes/county/SmartFilters";
import type { NursingHomeFilters } from "@/hooks/use-county-page-data";
import { Link } from "react-router-dom";
import { Euro, FileText, ArrowRight } from "lucide-react";

export default function AllNursingHomes() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCounty, setSelectedCounty] = useState<string | null>(null);
  const [expandedCounties, setExpandedCounties] = useState<string[]>([]);
  const [filters, setFilters] = useState<NursingHomeFilters>({
    inspectionRating: null,
    fairDealOnly: false,
    priceRange: null,
    category: null,
    maxDistanceKm: null,
    userLocation: null,
  });

  const activeFilterCount = [
    filters.inspectionRating,
    filters.fairDealOnly,
    filters.priceRange,
    filters.category,
    filters.maxDistanceKm,
  ].filter(Boolean).length;
  // Get all counties for the filter
  const { data: counties, isLoading: countiesLoading } = useCounties();

  // Query to get all nursing homes with filtering
  const { data: allNursingHomes, isLoading, error } = useQuery({
    queryKey: ['all_nursing_homes', searchQuery, selectedCounty],
    queryFn: async () => {
      try {
        console.log('Fetching all nursing homes with:', {
          search: searchQuery,
          county: selectedCounty
        });
        
        let query = supabase
          .from('nursing_homes')
          .select('*', { count: 'exact' });
          
        // Apply search filter if provided
        if (searchQuery) {
          query = query.ilike('center_name', `%${searchQuery}%`);
        }
        
        // Apply county filter if selected
        if (selectedCounty) {
          query = query.eq('county_slug', selectedCounty);
        }
        
        // Get all data for grouping by county
        const { data, count, error } = await query
          .order('county', { ascending: true })
          .order('center_name', { ascending: true });
        
        if (error) {
          throw error;
        }
        
        return {
          data: data || [],
          count: count || 0
        };
      } catch (err) {
        console.error('Error fetching nursing homes:', err);
        toast.error("Failed to load nursing homes. Please try again later.");
        throw err;
      }
    },
    placeholderData: (prevData) => prevData,
  });

  // Apply client-side smart filters
  const filteredData = useMemo(() => {
    if (!allNursingHomes?.data) return [];
    return allNursingHomes.data.filter((home) => {
      if (filters.inspectionRating && home.inspection_rating !== filters.inspectionRating) return false;
      if (filters.fairDealOnly && home.fair_deal_approved !== true) return false;
      if (filters.category && home.category !== filters.category) return false;
      if (filters.priceRange && home.price_range) {
        const match = home.price_range.match(/€([\d,]+)/);
        const minPrice = match ? parseInt(match[1].replace(",", ""), 10) : null;
        if (minPrice === null) return false;
        switch (filters.priceRange) {
          case "under_1000": if (minPrice >= 1000) return false; break;
          case "1000_1300": if (minPrice < 1000 || minPrice > 1300) return false; break;
          case "1300_1600": if (minPrice < 1300 || minPrice > 1600) return false; break;
          case "over_1600": if (minPrice < 1600) return false; break;
        }
      } else if (filters.priceRange) return false;
      if (filters.userLocation && filters.maxDistanceKm) {
        if (!home.latitude || !home.longitude) return false;
        const { lat, lng } = filters.userLocation;
        const R = 6371;
        const dLat = (home.latitude - lat) * Math.PI / 180;
        const dLng = (home.longitude - lng) * Math.PI / 180;
        const a = Math.sin(dLat / 2) ** 2 +
          Math.cos(lat * Math.PI / 180) * Math.cos(home.latitude * Math.PI / 180) *
          Math.sin(dLng / 2) ** 2;
        const dist = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        if (dist > filters.maxDistanceKm) return false;
      }
      return true;
    });
  }, [allNursingHomes, filters]);

  // Group nursing homes by county
  const nursingHomesByCounty = useMemo(() => {
    return filteredData.reduce((acc, home) => {
      const countyObj = counties?.find(
        (c) => c.slug === home.county_slug || c.name === home.county
      );
      const countyName = countyObj?.name || home.county || 'Other';
      if (!acc[countyName]) acc[countyName] = [];
      acc[countyName].push(home);
      return acc;
    }, {} as Record<string, typeof filteredData>);
  }, [filteredData, counties]);

  // Get sorted county names for display
  const countyNames = useMemo(() => {
    return Object.keys(nursingHomesByCounty).sort();
  }, [nursingHomesByCounty]);

  // Handle county filter selection
  const handleCountySelect = (countySlug: string | null) => {
    setSelectedCounty(countySlug);

    // If selecting a specific county, expand it
    if (countySlug && counties) {
      const county = counties.find(c => c.slug === countySlug)?.name;
      if (county && !expandedCounties.includes(county)) {
        setExpandedCounties(prev => [...prev, county]);
      }
    }
  };

  // Toggle county expansion
  const toggleCountyExpansion = (county: string) => {
    setExpandedCounties(prev => 
      prev.includes(county) 
        ? prev.filter(c => c !== county)
        : [...prev, county]
    );
  };
  
  return (
    <Layout>
      <Helmet>
        <title>All Nursing Homes in Ireland | Grandpal</title>
        <meta 
          name="description" 
          content="Browse our comprehensive directory of nursing homes across Ireland. Find detailed information about facilities, services, and care options for your loved ones."
        />
      </Helmet>

      <div className="bg-grandpal-beige py-12">
        <div className="container">
          <h1 className="text-4xl font-bold text-center mb-4">
            Nursing Homes in Ireland
          </h1>
          <p className="text-xl text-grandpal-gray text-center mb-8">
            Find the perfect nursing home for your loved ones
          </p>
        </div>
      </div>

      <div className="container py-12">
        <div className="mb-8">
          <SearchFilterBar
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            selectedCounty={selectedCounty}
            handleCountySelect={handleCountySelect}
            counties={counties?.map(c => ({ county: c.name, county_slug: c.slug }))}
            countiesLoading={countiesLoading}
          />
          <SmartFilters
            filters={filters}
            setFilters={setFilters}
            activeFilterCount={activeFilterCount}
          />
        </div>

        {/* Fair Deal funding cross-link banner */}
        <div className="mb-8 rounded-xl border border-grandpal-green/20 bg-grandpal-beige/60 p-5 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 rounded-lg bg-grandpal-green/10 p-2 shrink-0">
              <Euro className="h-5 w-5 text-grandpal-green" />
            </div>
            <div>
              <p className="font-semibold text-gray-900 text-sm leading-snug">
                Wondering how to fund nursing home care?
              </p>
              <p className="text-sm text-gray-600 mt-0.5">
                The Fair Deal Scheme can cover up to 80% of costs. Learn how it works and start your application.
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:shrink-0">
            <Link
              to="/fair-deal"
              className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-grandpal-green/40 px-4 py-2 text-sm font-medium text-grandpal-green hover:bg-grandpal-green/10 transition-colors whitespace-nowrap"
            >
              <Euro className="h-4 w-4" />
              Fair Deal Hub
            </Link>
            <Link
              to="/guides/fair-deal-application-ireland-2026"
              className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-grandpal-green px-4 py-2 text-sm font-medium text-white hover:bg-grandpal-green-dark transition-colors whitespace-nowrap"
            >
              <FileText className="h-4 w-4" />
              Application Guide
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>

        <CountyListings
          isLoading={isLoading}
          error={error as Error}
          nursingHomesByCounty={nursingHomesByCounty}
          countyNames={countyNames}
          expandedCounties={expandedCounties}
          toggleCountyExpansion={toggleCountyExpansion}
          allNursingHomes={allNursingHomes}
          searchQuery={searchQuery}
          selectedCounty={selectedCounty}
          counties={counties && counties.map(c => ({ county: c.name, county_slug: c.slug }))}
        />
      </div>
      
      <CTASection />
    </Layout>
  );
}
